import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Users, Zap, Bell, BookOpen, StickyNote, Sparkles, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  rank_up: { icon: TrendingUp, tone: "success", label: "Rank up" },
  rank_down: { icon: TrendingDown, tone: "danger", label: "Rank drop" },
  rank_enter: { icon: TrendingUp, tone: "success", label: "Entered" },
  rank_exit: { icon: TrendingDown, tone: "danger", label: "Exit" },
  boundary_top10: { icon: TrendingUp, tone: "accent", label: "Top 10" },
  boundary_out10: { icon: TrendingDown, tone: "danger", label: "Out of top 10" },
  boundary_top3: { icon: TrendingUp, tone: "success", label: "Top 3" },
  google_update: { icon: Zap, tone: "warning", label: "Google update" },
  volatility: { icon: Zap, tone: "warning", label: "Volatility" },
  note: { icon: StickyNote, tone: "accent", label: "Note" },
  recommendation: { icon: Sparkles, tone: "accent", label: "AI recommendation" },
  alert_major_drop: { icon: Bell, tone: "danger", label: "Alert · major drop" },
  alert_major_gain: { icon: Bell, tone: "success", label: "Alert · major gain" },
  alert_entered_top10: { icon: Bell, tone: "success", label: "Alert · top 10" },
  alert_entered_top3: { icon: Bell, tone: "success", label: "Alert · top 3" },
  alert_competitor_overtake: { icon: Users, tone: "warning", label: "Competitor overtake" },
};

const TONE_CLS = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  danger: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  accent: "bg-[#E0E7FF] text-[#0047FF] border-[#0047FF]/20",
};

export default function KeywordTimeline({ keywordId }) {
  const [data, setData] = useState(null);
  const [showAddNote, setShowAddNote] = useState(false);
  const [note, setNote] = useState({ title: "", body: "", date: "" });

  const load = () => api.get(`/timeline/keyword/${keywordId}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [keywordId]); // eslint-disable-line

  const addNote = async (e) => {
    e.preventDefault();
    try {
      const k = data;
      await api.post("/timeline/notes", {
        keyword_id: keywordId,
        project_id: undefined,
        date: note.date || undefined,
        title: note.title,
        body: note.body,
      }).catch(async () => {
        // Need project_id from parent — fetch keyword to get it
        const kw = await api.get(`/keywords/${keywordId}`);
        return api.post("/timeline/notes", {
          keyword_id: keywordId,
          project_id: kw.data.project_id,
          date: note.date || undefined,
          title: note.title,
          body: note.body,
        });
      });
      toast.success("Note added.");
      setNote({ title: "", body: "", date: "" });
      setShowAddNote(false);
      load();
    } catch {
      toast.error("Failed to add note.");
    }
  };

  const delNote = async (noteId) => {
    await api.delete(`/timeline/notes/${noteId}`);
    load();
  };

  if (!data) return <div className="text-sm text-[#8A94A6]">Loading timeline…</div>;

  const events = [...data.events].reverse(); // newest first

  return (
    <div data-testid="timeline">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium">Timeline · {events.length} events</div>
        <button onClick={() => setShowAddNote(true)} data-testid="timeline-add-note" className="btn-ghost px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1"><Plus className="h-3 w-3" /> Add note</button>
      </div>

      {showAddNote && (
        <form onSubmit={addNote} className="card-crisp p-3 mb-3 space-y-2" data-testid="timeline-note-form">
          <input required data-testid="note-title" placeholder="Event title (e.g. Refreshed H2s)" value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded-md text-sm" />
          <textarea rows="2" data-testid="note-body" placeholder="Details (optional)" value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded-md text-sm" />
          <div className="flex items-center gap-2">
            <input type="date" data-testid="note-date" value={note.date} onChange={(e) => setNote({ ...note, date: e.target.value })} className="px-2 py-1 border border-[#E2E8F0] rounded-md text-xs" />
            <button data-testid="note-save" className="btn-primary px-3 py-1 rounded-md text-xs">Save</button>
            <button type="button" onClick={() => setShowAddNote(false)} className="text-xs text-[#525B6A]">Cancel</button>
          </div>
        </form>
      )}

      <div className="relative border-l-2 border-[#E2E8F0] ml-3 pl-5 space-y-4">
        {events.map((e, i) => {
          const meta = KIND_META[e.kind] || { icon: BookOpen, tone: "accent", label: e.kind };
          const Icon = meta.icon;
          const toneCls = TONE_CLS[meta.tone] || TONE_CLS.accent;
          return (
            <div key={i} className="relative" data-testid={`timeline-event-${e.kind}`}>
              <div className={`absolute -left-[34px] top-0 h-7 w-7 rounded-full border-2 border-white flex items-center justify-center ${toneCls}`}>
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div className="card-crisp p-3">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">{meta.label} · {e.date}</div>
                  {e.meta?.note_id && (
                    <button onClick={() => delNote(e.meta.note_id)} className="text-[#8A94A6] hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                  )}
                </div>
                <div className="mt-1 text-sm text-[#0A0D14] font-medium">{e.title}</div>
                {e.body && <div className="mt-1 text-xs text-[#525B6A] leading-relaxed">{e.body}</div>}
                {e.meta?.reasons && (
                  <div className="mt-2 border-t border-[#E2E8F0] pt-2 space-y-1">
                    {e.meta.reasons.slice(0, 2).map((r, j) => <div key={j} className="text-xs text-[#525B6A]">· {r}</div>)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {events.length === 0 && (
          <div className="text-sm text-[#8A94A6] py-6">No events yet. Refresh rankings or add a note.</div>
        )}
      </div>
    </div>
  );
}
